import { slugify } from './slug.js';
import { loosenVowels, stemPlural } from './dutch.js';
/**
 * How well a product's own title answers the word somebody typed.
 *
 * The problem this exists for: searching *sinaasappel* returned drinking
 * yoghurt before it returned an orange. Every shop's search engine is built to
 * sell, so a query is matched against the whole title and a flavour word counts
 * for as much as the head noun — "Optimel Drinkyoghurt Sinaasappel" is a
 * perfectly good match for "sinaasappel" and a perfectly useless answer to the
 * question. The four shops disagree about the details but they all do this, so
 * fixing it in one adapter fixes nothing.
 *
 * The rule a person is actually applying, and all this encodes, is: **the
 * product whose name is mostly the thing I asked for beats the product that
 * merely mentions it.** Three signals carry that, and none of them needs to
 * know what a sinaasappel is:
 *
 *   - *where* the word falls. A query word at the front of a title is the head
 *     noun; at the back it is a flavour or a variety.
 *   - *how much else* the title says. Each unmatched word is one more qualifier
 *     standing between the query and the product.
 *   - *which end of a compound* it sits on. Dutch compounds are head-final: a
 *     `handsinaasappel` is an orange, a `sinaasappellimonade` is squash.
 *
 * Deliberately **not** an LLM call, and that is a decision rather than an
 * omission — see `docs/AI.md` §1. This runs on every keystroke-debounced
 * search, in an aisle, on a phone: a model call would add a second of latency
 * and a cost to the one interaction that has to feel instant, to re-rank
 * twenty rows whose ordering three cheap signals already fix. The AI boundary
 * in ARCHITECTURE also says ranking is not one of the model's jobs.
 *
 * It is a **sort key, never a filter**. Everything the shops returned is still
 * shown, and a query this scores at zero across the board leaves the upstream
 * order exactly as it was — a bad scorer must degrade to today's behaviour
 * rather than to an empty screen.
 */
/** Words, diacritics folded, punctuation gone: "Crème 1,5L" → ["creme", "1", "5l"]. */
function tokenize(text) {
    return slugify(text).split('-').filter(Boolean);
}
/**
 * Pack sizes, units and packaging: "2", "kg", "1l", "net", "stuks".
 *
 * These are dropped before anything is scored, because two of the three
 * signals below are *positions within the title* and a trailing "2 kg" moves
 * the head of the title without saying anything about the product.
 * `Sinaasappelen net 1 kg` is a title of one word wearing three more.
 */
const NOISE = /^(\d+([.,]\d+)?(l|ml|cl|dl|kg|g|gr|mg)?|l|ml|cl|dl|kg|g|gr|mg|st|stk|stuk|stuks|pak|pack|blik|fles|zak|doos|bos|krat|net|bak|bakje)$/;
/** The words that actually name the product. Never empty — a title of pure size keeps it. */
function substantive(tokens) {
    const kept = tokens.filter((t) => !NOISE.test(t));
    return kept.length > 0 ? kept : tokens;
}
/*
 * The morphology lives in `dutch.ts`, shared with `identity.ts` — same
 * language, two appetites for risk. Ranking may match loosely (a wrong order
 * costs a scroll); identity may not (a wrong match costs the household the
 * wrong product), so identity uses the smaller `fold` and never these two.
 */
const stem = stemPlural;
const loosen = loosenVowels;
/**
 * How well one word of a title answers one word of a query.
 *
 * **Dutch compounds are head-final, and which end the query word sits on is
 * the whole answer.** A *handsinaasappel* is a sinaasappel — the query word is
 * the head and the rest is a qualifier. A *sinaasappellimonade* is a limonade;
 * the query word is the modifier and the product is something else made out of
 * the thing that was asked for. Same word, same compound, opposite meanings,
 * and only the position distinguishes them.
 *
 * Getting this backwards is not a subtle mis-weighting. Probed live against
 * the four shops, *sinaasappel* returns `Handsinaasappels`, `Perssinaasappels`
 * and `Jumbo Handsinaasappelen` as the only actual oranges on offer, against
 * `Sinaasappellimonade`, `Sinaasappelhoning` and a quark — so scoring the
 * modifier position above the head position puts every real answer below every
 * wrong one, which is the reported bug with extra steps.
 *
 * 1 the same word · 0.95 a kind of it · 0.5 something made from it · 0.4 a
 * chance substring.
 *
 * A head-final compound scores just below an exact match rather than equal to
 * it. It *is* the thing — but `Melk` should still beat `Chocolademelk` for
 * "melk", and only that last twentieth separates them.
 */
function tokenScore(queryToken, nameToken) {
    if (queryToken === nameToken)
        return 1;
    const q = stem(queryToken);
    const n = stem(nameToken);
    if (q === n || loosen(q) === loosen(n))
        return 1;
    // "handsinaasappel", "perssinaasappel": a *kind of* the thing asked for.
    if (n.endsWith(q) || nameToken.endsWith(queryToken))
        return 0.95;
    // "sinaasappelsap", "sinaasappellimonade": the thing asked for, turned into
    // something else. A real match, and not what was wanted.
    if (n.startsWith(q) || nameToken.startsWith(queryToken))
        return 0.5;
    if (nameToken.includes(queryToken) && queryToken.length >= 3)
        return 0.4;
    return 0;
}
/**
 * Roughly 0 (nothing in common) to 1.75 (the title *is* the query). The
 * absolute value means nothing; only the ordering between two candidates for
 * the same query does.
 */
export function relevanceScore(name, query) {
    const q = substantive(tokenize(query));
    const n = substantive(tokenize(name));
    if (q.length === 0 || n.length === 0)
        return 0;
    let covered = 0;
    let matchedWords = 0;
    let firstHit = n.length;
    let lastHit = -1;
    for (const queryToken of q) {
        let best = 0;
        let bestIndex = -1;
        for (let i = 0; i < n.length; i++) {
            const score = tokenScore(queryToken, n[i]);
            if (score > best) {
                best = score;
                bestIndex = i;
            }
        }
        covered += best;
        if (best > 0) {
            matchedWords++;
            if (bestIndex < firstHit)
                firstHit = bestIndex;
            if (bestIndex > lastHit)
                lastHit = bestIndex;
        }
    }
    const coverage = covered / q.length;
    // Nothing matched at all: score nothing rather than a small number, so a
    // stable sort leaves these where the shop put them.
    if (coverage === 0)
        return 0;
    let score = coverage;
    /*
     * The title and the query are the same thing. This is the orange.
     *
     * `coverage === 1` is the load-bearing half and it is not the same as "every
     * query word found something": a one-word title matches a one-word query on
     * word *count* however weak the match was, so without the exactness test
     * `Sinaasappellimonade` collected the whole-title bonus for being a single
     * token and outscored three actual oranges.
     */
    if (coverage === 1 && n.length === q.length && firstHit === 0)
        score += 0.5;
    // Every word that is not part of the answer is a qualifier around it.
    // Capped, because past half a dozen extra words the title is just long.
    score -= Math.min(0.3, Math.max(0, n.length - matchedWords) * 0.05);
    /*
     * Position, read from **both** ends, because a Dutch product title has two
     * patterns and each end catches the one the other misses:
     *
     *   - *brand first, flavour last* — "Optimel Drinkyoghurt Sinaasappel",
     *     "Campina Vla Sinaasappel". The query word trailing means it is what
     *     the product tastes of, not what it is. Only the distance from the
     *     **front** separates these from the fruit.
     *   - *modifier first, head last* — "Appel-caramel cake", "Kaasfondue
     *     pakket". Dutch noun phrases are head-final, so the query word leading
     *     means the product is something else. Only the distance from the
     *     **back** separates these from "Giga appels".
     *
     * Rewarding one end alone gets the other pattern exactly wrong, which is how
     * a cake came back first for "appel": it scored identically to
     * `Kinderappels` on the front-only rule and won on the shops' own order.
     * Together, only a title where the query is near both ends — which is to say
     * a title that is mostly the query — collects the pair.
     *
     * The two weights are deliberately **not** equal. Split evenly, a word one
     * place from the front and a word one place from the back collect exactly
     * the same total, so `AH Sinaasappel handpers` and `Campina Vla Sinaasappel`
     * tie — oranges level with custard. The front is worth slightly more because
     * the trailing flavour is the commoner pattern by far: every flavoured
     * yoghurt, vla, quark and juice in a Dutch supermarket is named this way,
     * and there are far more of those than there are cakes. It is a tie-breaker,
     * not a strong claim; the gap is small on purpose.
     */
    score += 0.17 * (1 - firstHit / n.length);
    score += 0.13 * ((lastHit + 1) / n.length);
    return score;
}
/**
 * Sort a page of results by how well each answers the query, **stably**: two
 * rows the scorer cannot separate stay in the order they arrived in, which for
 * live search is upstream relevance within each shop, shop by shop.
 */
export function byRelevance(items, query, nameOf) {
    const scored = items.map((item, index) => ({ item, index, score: relevanceScore(nameOf(item), query) }));
    scored.sort((a, b) => (b.score - a.score) || (a.index - b.index));
    return scored.map((s) => s.item);
}
/**
 * Whether a shop's title **is** the thing a product name names, not merely
 * made from it — a filter, unlike everything above, and so a much narrower
 * question.
 *
 * The head of the name decides: its last substantive word, because Dutch noun
 * phrases are head-final the same way its compounds are ("halfvolle melk" is
 * melk). That word must be a word of the title, or the head of one of its
 * compounds — `Dole Bananen` and `Handsinaasappels` are the thing,
 * `Bananenbroodmix` and `Sinaasappellimonade` are something made from it.
 *
 * Used where the app is about to *offer* a product as the answer: the
 * comparison of a known product and the card's "Welke wordt het?". A ranking
 * may be loose because a wrong order costs a scroll; an offer may not, because
 * a wrong one goes on the list.
 */
export function namesTheThing(title, productName) {
    const name = substantive(tokenize(productName));
    const head = name[name.length - 1];
    if (!head)
        return true;
    return substantive(tokenize(title)).some((t) => tokenScore(head, t) >= 0.95);
}
//# sourceMappingURL=relevance.js.map